import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../api/Createapi.js";
import "./AuthModal.css";
import {
    FaUser, FaLock, FaEye, FaEyeSlash,
    FaArrowRight, FaBolt, FaShieldAlt, FaHeadset,
    FaTimes, FaEnvelope, FaPaperPlane, FaArrowLeft,
    FaKey, FaWifi
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../Loader";

/*
  AuthModal handles two views in one popup:
    view === "login"   → Login form
    view === "forgot"  → Forgot Password form
  
  Props:
    onClose()  — called to close the modal
*/
function AuthModal({ onClose }) {
    const [view, setView] = useState("login"); // "login" | "forgot"

    // Login state
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loginError, setLoginError] = useState("");
    const [loginLoading, setLoginLoading] = useState(false);
    const navigate = useNavigate();

    // Forgot Password state
    const [email, setEmail] = useState("");
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotSubmitted, setForgotSubmitted] = useState(false);

    /* ── Login handler ── */
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginLoading(true);
        setLoginError("");
        try {
            const res = await API.post("/login.php", { username, password });
            if (res.data.status === "success") {
                const role = res.data.user.role;
                sessionStorage.setItem("role", role);
                sessionStorage.setItem("username", res.data.user.username);
                sessionStorage.setItem("status", res.data.user.status);
                sessionStorage.setItem("fullname", res.data.user.fullname);
                sessionStorage.setItem("email", res.data.user.email);
                sessionStorage.setItem("mobile", res.data.user.mobile);
                sessionStorage.setItem("loginTime", Date.now());
                toast.success("Login Successful!");
                onClose();
                setTimeout(() => {
                    navigate(role === "admin" ? "/admin" : "/user");
                }, 600);
            } else {
                setLoginError("Invalid Username or Password");
                toast.error("Invalid Username or Password");
            }
        } catch (err) {
            console.error(err);
            setLoginError("Server Error. Please try again.");
            toast.error("Server Connection Error");
        } finally {
            setLoginLoading(false);
        }
    };

    /* ── Forgot Password handler ── */
    const handleForgot = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        try {
            const res = await API.post("/forgot_password.php", { email });
            if (res.data.status === "success") {
                setForgotSubmitted(true);
                toast.success("Reset link sent to your email!");
            } else {
                toast.error("Email not found in our records.");
            }
        } catch (err) {
            console.error(err);
            toast.error("Server error. Please try again.");
        } finally {
            setForgotLoading(false);
        }
    };

    const switchToForgot = () => {
        setLoginError("");
        setView("forgot");
    };

    const switchToLogin = () => {
        setForgotSubmitted(false);
        setEmail("");
        setView("login");
    };

    /* ── Close on backdrop click ── */
    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="auth-modal-overlay" onClick={handleOverlayClick}>
            {(loginLoading || forgotLoading) && <Loader />}

            <div className="auth-modal-card">
                {/* ── LEFT HERO PANEL ── */}
                <div className="auth-modal-hero">
                    <button className="hero-back-btn" onClick={onClose} type="button" title="Back to page">
                        <FaArrowLeft /> Back
                    </button>
                    <div className="auth-modal-glow auth-modal-glow-1" />
                    <div className="auth-modal-glow auth-modal-glow-2" />

                    <div className="auth-modal-hero-content">
                        <div className="auth-modal-brand">
                            RiTU <span>BROAD BAND</span>
                        </div>

                        {view === "login" ? (
                            <>
                                <h2 className="auth-modal-hero-title">Ultra-Fast Fiber Internet</h2>
                                <p className="auth-modal-hero-desc">
                                    Connect to seamless high-speed internet, manage your subscription, and track payments instantly.
                                </p>
                                <div className="auth-modal-features">
                                    <div className="auth-modal-feature-item">
                                        <div className="auth-modal-feature-icon"><FaBolt /></div>
                                        <span>Bandwidth up to 1 Gbps</span>
                                    </div>
                                    <div className="auth-modal-feature-item">
                                        <div className="auth-modal-feature-icon"><FaShieldAlt /></div>
                                        <span>99.9% Uptime Guarantee</span>
                                    </div>
                                    <div className="auth-modal-feature-item">
                                        <div className="auth-modal-feature-icon"><FaHeadset /></div>
                                        <span>24/7 Priority Support</span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <h2 className="auth-modal-hero-title">Account Security</h2>
                                <p className="auth-modal-hero-desc">
                                    Don't worry! We'll help you regain access to your account swiftly and securely.
                                </p>
                                <div className="auth-modal-features">
                                    <div className="auth-modal-feature-item">
                                        <div className="auth-modal-feature-icon"><FaKey /></div>
                                        <span>Encrypted Recovery</span>
                                    </div>
                                    <div className="auth-modal-feature-item">
                                        <div className="auth-modal-feature-icon"><FaBolt /></div>
                                        <span>Instant Email Verification</span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* ── RIGHT FORM PANEL ── */}
                <div className="auth-modal-form-panel">

                    {/* ═══ LOGIN VIEW ═══ */}
                    {view === "login" && (
                        <>
                            <div className="auth-modal-header">
                                <h2>Welcome Back</h2>
                                <p>Sign in to your Ritu Broad Band account</p>
                            </div>

                            <form className="auth-modal-form" onSubmit={handleLogin}>
                                <div className="auth-modal-input-group">
                                    <label htmlFor="modal-username">Username or Mobile No.</label>
                                    <div className="auth-modal-input-wrap">
                                        <span className="auth-modal-input-icon"><FaUser /></span>
                                        <input
                                            id="modal-username"
                                            type="text"
                                            placeholder="Enter your username or mobile no."
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="auth-modal-input-group">
                                    <div className="auth-modal-label-row">
                                        <label htmlFor="modal-password">Password</label>
                                        <span className="auth-modal-link" onClick={switchToForgot}>
                                            Forgot Password?
                                        </span>
                                    </div>
                                    <div className="auth-modal-input-wrap">
                                        <span className="auth-modal-input-icon"><FaLock /></span>
                                        <input
                                            id="modal-password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder="Enter your password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                        />
                                        <span
                                            className="auth-modal-eye"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </span>
                                    </div>
                                </div>

                                {loginError && <div className="auth-modal-error">{loginError}</div>}

                                <button type="submit" className="auth-modal-btn" disabled={loginLoading}>
                                    {loginLoading ? "Signing in..." : <>Sign In <FaArrowRight /></>}
                                </button>
                            </form>

                            <div className="auth-modal-footer">
                                New connection?{" "}
                                <span
                                    className="auth-modal-footer-link"
                                    onClick={() => { onClose(); setTimeout(() => document.getElementById("contactFormSection")?.scrollIntoView({ behavior: "smooth" }), 300); navigate("/"); }}
                                >
                                    <FaWifi /> Request for Wi-Fi Connection
                                </span>
                            </div>

                            {/* Terms & Conditions notice below login */}
                            <p style={{ fontSize: "0.75rem", color: "#94a3b8", textAlign: "center", marginTop: "10px", lineHeight: "1.5" }}>
                                By signing in, you agree to our{" "}
                                <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: "#0B72B9", fontWeight: "600", textDecoration: "underline" }}>
                                    Terms &amp; Conditions
                                </a>
                            </p>
                        </>
                    )}

                    {/* ═══ FORGOT PASSWORD VIEW ═══ */}
                    {view === "forgot" && (
                        <>
                            <div className="auth-modal-header">
                                <h2>Forgot Password?</h2>
                                <p>Enter your registered email to receive a reset link.</p>
                            </div>

                            {!forgotSubmitted ? (
                                <form className="auth-modal-form" onSubmit={handleForgot}>
                                    <div className="auth-modal-input-group">
                                        <label htmlFor="modal-email">Registered Email</label>
                                        <div className="auth-modal-input-wrap">
                                            <span className="auth-modal-input-icon"><FaEnvelope /></span>
                                            <input
                                                id="modal-email"
                                                type="email"
                                                placeholder="Enter your email address"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <button type="submit" className="auth-modal-btn" disabled={forgotLoading}>
                                        {forgotLoading ? "Sending..." : <><FaPaperPlane /> Send Reset Link</>}
                                    </button>
                                </form>
                            ) : (
                                <div className="auth-modal-success-box">
                                    <div className="auth-modal-success-icon"><FaEnvelope /></div>
                                    <h3>Check Your Inbox</h3>
                                    <p>
                                        We've sent a reset link to <strong>{email}</strong>. Please check your inbox.
                                    </p>
                                    <button
                                        className="auth-modal-btn auth-modal-btn-secondary"
                                        onClick={() => setForgotSubmitted(false)}
                                    >
                                        Resend Link
                                    </button>
                                </div>
                            )}

                            <div className="auth-modal-footer">
                                <span className="auth-modal-footer-link" onClick={switchToLogin}>
                                    <FaArrowLeft /> Back to Sign In
                                </span>
                            </div>
                        </>
                    )}

                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={4000}
                hideProgressBar={false}
                closeOnClick
                draggable
                pauseOnHover
                theme="colored"
                style={{ zIndex: 99999 }}
            />
        </div>
    );
}

export default AuthModal;
